import classNames from "classnames"
import { formatFeedTime } from "@nova/format"
import { Comment } from "@nova/schemas"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { CommentBody } from "./CommentBody"
import { AddReactionButton, useCommentReactions } from "./CommentReactions"
import { CommentMoreMenu } from "./CommentMoreMenu"
import { ReplyThreadButton } from "./ReplyThreadButton"

export const MapCommentListCard = ({
    comment,
    isActive = false,
    onClick,
}: {
    comment: Comment<"map-pin" | "map-comment">
    isActive?: boolean
    onClick?: () => void
}) => {
    const isUnread = comment.isUnread
    const { fullName } = useFullnameForUserId(comment.createdBy)
    const { pickerOpen, setPickerOpen, addButtonInHeader } = useCommentReactions(comment)
    const controlsRevealed = isActive || pickerOpen

    return (
        <div
            className={classNames(
                "group/comment relative flex flex-col gap-2 px-4 py-3 w-full",
                isActive && "bg-white-100",
                onClick && "cursor-pointer hover:bg-white-100"
            )}
            onClick={onClick}
        >
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <div className="h-8 w-8 shrink-0">
                        <ProfilePhoto
                            userId={comment.createdBy}
                            fullName={fullName || ""}
                            className="rounded-lg"
                        />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <div className="relative h-5 w-5 shrink-0">
                            {isUnread && (
                                <div
                                    className={classNames(
                                        "absolute inset-0 flex items-center justify-center",
                                        controlsRevealed
                                            ? "opacity-0"
                                            : "group-hover/comment:opacity-0"
                                    )}
                                >
                                    <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                                </div>
                            )}
                            <div
                                className={classNames(
                                    "absolute inset-0",
                                    !controlsRevealed && "opacity-0 group-hover/comment:opacity-100"
                                )}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <CommentMoreMenu comment={comment} mapEntityId={comment.entityId} />
                            </div>
                        </div>
                        {addButtonInHeader && (
                            <div
                                className={classNames(
                                    !controlsRevealed && "opacity-0 group-hover/comment:opacity-100"
                                )}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <AddReactionButton comment={comment} onOpenChange={setPickerOpen} />
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-col min-w-0">
                    <h5 className="text-white truncate">{fullName}</h5>
                    <small className="text-gray-300">{formatFeedTime(comment.createdAt)}</small>
                </div>
            </div>
            <CommentBody comment={comment} onPickerOpenChange={setPickerOpen} />
            {!!comment.numReplies && (
                <ReplyThreadButton
                    numReplies={comment.numReplies}
                    participantIds={comment.participantIds}
                />
            )}
        </div>
    )
}
